<template>
    <default-layout>
        <section class="px-4 md:px-10 py-4 md:py-7 space-y-7">
            <custom-info-header
                icon="hugeicons:contact-01"
                title="Contacts"
                description="Directory of contacts added to your company">
                <template  v-slot:action>
                    <custom-button
                        type="button"
                        size="md"
                        label="Add Contact"
                        @click="() => isCreateContactModal = true"
                    />
                </template>
            </custom-info-header>
            
            <custom-table :headers="[
                                'Name',
                                'Email',
                                'Phone',
                                'Brand',
                                'Created Date',
                                ''
                            ]"
                            :loading="{
                                isLoading: isLoading,
                                barRows: 6,
                                barCols: 5
                            }"
                            :isEmpty="!isLoading && contacts.length <= 0">
                            <template v-slot:body>
                                <tr v-for="contact in contacts" :key="contact.id" class="h-16 border-t border-gray-100 rounded">
                                    <custom-table-td>
                                        <router-link :to="{ name: 'updateContact', params: { guid: contact.guid }}" class="text-blue-600 flex items-center gap-1 hover:underline">
                                            <em class="not-italic">{{ contact.name }}</em>
                                            <Icon icon="lucide:external-link" />
                                        </router-link>
                                    </custom-table-td>
                                    <custom-table-td>{{ contact.email }}</custom-table-td>
                                    <custom-table-td>{{ contact.phone }}</custom-table-td>
                                    <custom-table-td>{{ contact.brand.name }}</custom-table-td>
                                    <custom-table-td>{{ formatDate(contact.createdDate) }}</custom-table-td>
                                    <custom-table-td type="template">
                                        <custom-dropdown placement="right">
                                            <template v-slot:button>
                                                <button class="flex px-5 py-3 rounded-md bg-transparent text-gray-500">
                                                    <Icon icon="mingcute:more-2-fill" class="text-xl" />
                                                </button>
                                            </template>
                                            <template v-slot:content>
                                                <section>
                                                    <custom-dropdown-item type="link" label="Update" :to="{ name: 'updateContact', params: { guid: contact.guid }}"/>
                                                </section>
                                            </template>
                                        </custom-dropdown>
                                    </custom-table-td>
                                </tr>
                            </template>
            </custom-table>
        </section>

        <custom-modal 
                title="Contact"
                subTitle="Directory of contacts added to your company" 
                :is-open="isCreateContactModal"
                @cancel="() => isCreateContactModal = false">
                <template v-slot:body>
                    <form @submit.stop.prevent="onCreateHandle" class="grid grid-cols-2 gap-x-4 gap-y-3">
                        <div class="col-span-full">
                            <custom-input
                                    id="contactName"
                                    label="Name"
                                    type="text"
                                    v-model="contact.name"
                                    placeholder="Name"
                                    :validate="validate$.name"
                            />
                        </div>

                        <div>
                            <custom-input
                                    id="contactEmail"
                                    type="email"
                                    label="Email"
                                    v-model="contact.email"
                                    placeholder="Email"
                                    :validate="validate$.email"
                            />
                        </div>

                        <div>
                            <custom-input
                                    id="contactPhone"
                                    type="phone"
                                    label="Phone"
                                    v-model="contact.phone"
                                    placeholder="Phone"
                                    :validate="validate$.phone"
                            />
                        </div>

                        <div class="col-span-full">
                            <custom-select
                                    id="brandId"
                                    label="Brand"
                                    placeholder="Select a brand"
                                    :data="brands"
                                    :setLabel="(model) => model.name"
                                    :setCode="(model) => model"
                                    v-model="contact.brand"
                                    :isLoading="isBrandLoading"
                                    :validate="validate$.brand"
                                />
                        </div>

                        <div class="col-span-full">
                            <custom-button 
                                type="submit"
                                :isLoading="isCreateLoading"
                                label="Create"
                                class="float-right"
                                size="xs"
                            />
                        </div>
                    </form>
                </template>
        </custom-modal>
    </default-layout>
</template>
<script setup>
import { onMounted } from 'vue';
import { Icon } from "@iconify/vue";
import { formatDate } from '@/core/helper/utility.js'
import { useContact } from '@/core/composables/useContact';
import { useBrand } from '@/core/composables/useBrand';
import DefaultLayout from '@/app/layouts/DefaultLayout';
import CustomInfoHeader from '@/app/components/CustomInfoHeader';
import CustomTable from '@/app/components/CustomTable';
import CustomTableTd from '@/app/components/CustomTableTd'
import CustomModal from '@/app/components/CustomModal'
import CustomInput from '@/app/components/CustomInput'
import CustomDropdown from '@/app/components/CustomDropdown'
import CustomSelect from '@/app/components/CustomSelect'
import CustomButton from '@/app/components/CustomButton';
import CustomDropdownItem from '@/app/components/CustomDropdownItem'

const {
    validate$,
    isLoading,
    isCreateLoading,
    isCreateContactModal,
    contacts,
    contact,
    getContacts,
    onCreateHandle
} = useContact();

const {
    isLoading: isBrandLoading,
    brands,
    getBrands
} = useBrand(); 

onMounted(async () => {
    await getContacts();
    await getBrands();
});
</script>